import { useCallback, useEffect, useMemo, useState } from 'react'

import type { ChatItemAttachment } from '@/models/agentConversation'

/** A first message handed to the panel by another surface, e.g. a page's inline composer. */
export interface HandedOverPrompt {
  text: string
  documentIds: string[]
  attachments: ChatItemAttachment[]
  /** Leave it in the composer for the user to finish and send, rather than sending it. */
  draft?: boolean
}

export interface AgentPanelState {
  open: boolean
  toggle: () => void
  close: () => void
  /** The conversation the panel is showing; undefined until one is picked. */
  activeId: string | undefined
  setActiveId: (id: string | undefined) => void
  /**
   * Sent by the panel as soon as its thread is ready, then cleared. Deliberately
   * not persisted: a message the user thought they sent must not resurface on a
   * reload having never been sent.
   */
  handedOverPrompt: HandedOverPrompt | undefined
  setHandedOverPrompt: (prompt: HandedOverPrompt | undefined) => void
}

/**
 * Open/closed and which thread, for an agent side panel.
 *
 * Held above the router `Outlet` by whichever provider uses it — that is what
 * lets a chat stay open, and its conversation stay selected, while the user
 * navigates.
 *
 * Mirrored into `sessionStorage` under `storagePrefix` so a reload doesn't
 * close it either. Read in an effect rather than a lazy initialiser so the
 * first client render still matches the server's.
 */
export function useAgentPanelState(storagePrefix: string): AgentPanelState {
  const openKey = `${storagePrefix}.open`
  const activeIdKey = `${storagePrefix}.activeId`
  const [open, setOpen] = useState(false)
  const [activeId, setActiveIdState] = useState<string | undefined>(undefined)
  const [handedOverPrompt, setHandedOverPrompt] = useState<HandedOverPrompt | undefined>(undefined)

  useEffect(() => {
    setOpen(sessionStorage.getItem(openKey) === 'true')
    setActiveIdState(sessionStorage.getItem(activeIdKey) ?? undefined)
  }, [openKey, activeIdKey])

  const toggle = useCallback(() => {
    setOpen((prev) => {
      sessionStorage.setItem(openKey, String(!prev))
      return !prev
    })
  }, [openKey])

  const close = useCallback(() => {
    sessionStorage.setItem(openKey, 'false')
    setOpen(false)
  }, [openKey])

  const setActiveId = useCallback(
    (id: string | undefined) => {
      if (id) sessionStorage.setItem(activeIdKey, id)
      else sessionStorage.removeItem(activeIdKey)
      setActiveIdState(id)
    },
    [activeIdKey]
  )

  return useMemo(
    () => ({
      open,
      toggle,
      close,
      activeId,
      setActiveId,
      handedOverPrompt,
      setHandedOverPrompt,
    }),
    [open, toggle, close, activeId, setActiveId, handedOverPrompt]
  )
}
